/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * One lead card in the list: plate + status, source + created time, the last
 * follow-up summary, the customer line, and the target-date / owner footer.
 */

import { Typography } from '@euler/app/components/typography';
import {
  IntentionChip,
  LD,
  cardShadowStyle,
} from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import {
  describeLeadActivity,
  isLeadClosingActivity,
} from '@euler/app/flows/leads/pages/detail/functions/renderLeadActivity';
import {
  formatLeadCreatedTime,
  formatLeadDate,
  formatLeadReachTime,
} from '@euler/app/flows/leads/pages/list/functions/formatLeadCardTime';
import { StatusPill, StatusTone } from '@euler/components/StatusPill';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  Lead,
  LeadOriginLabel,
  LeadStatus,
  LeadStatusLabel,
  isTerminalLeadStatus,
} from '@euler/model/lead/Lead';
import {
  formatLeadReachSummary,
  parseLeadActivityData,
} from '@euler/model/lead/LeadActivity';
import { formatVehicleBrandModel } from '@euler/utils/vehicle/brandModel';
import { ReactNode, memo, useCallback } from 'react';
import { TouchableOpacity, View } from 'react-native';

export const leadStatusTone = (status: LeadStatus): StatusTone => {
  switch (status) {
    case 'pending':
      return 'neutral';
    case 'due':
      return 'info';
    case 'working':
      return 'active';
    case 'booked':
      return 'info';
    case 'converted':
      return 'success';
    case 'lost':
    case 'cancelled':
    case 'expired':
      return 'danger';
  }
};

const Hairline = memo(() => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        height: 1,
        backgroundColor: LD.hairline,
        marginVertical: rpx(11),
      }}
    />
  );
});

/** A label/value pair occupying one side of a row (value aligns to `align`). */
const MetaBlock = memo(
  ({
    label,
    value,
    align = 'left',
  }: {
    label: string;
    value: ReactNode;
    align?: 'left' | 'right';
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={{ alignItems: align === 'right' ? 'flex-end' : 'flex-start' }}
      >
        <Typography.Text size={rpx(12)} color={LD.mist}>
          {label}
        </Typography.Text>
        <Typography.Text size={rpx(14)} color={LD.ink} medium mt={rpx(3)}>
          {value}
        </Typography.Text>
      </View>
    );
  },
);

export const LeadCell = memo(
  ({ lead, onPress }: { lead: Lead; onPress: (lead: Lead) => void }) => {
    const { rpx } = useResponsiveContext();
    const onCellPress = useCallback(() => onPress(lead), [lead, onPress]);
    // A closed lead shows how it closed rather than its last outreach, when
    // that closing step is still its latest activity.
    const closingData = isTerminalLeadStatus(lead.status)
      ? parseLeadActivityData(lead.lastActivityMeta)
      : null;
    const closed = isLeadClosingActivity(closingData);
    const reachSummary = closed
      ? describeLeadActivity(closingData!)
      : formatLeadReachSummary(lead.lastReachMeta);
    const reachAt = closed ? lead.lastActivityAt : lead.lastReachAt;

    // Brand · model beside the plate (normalized so the brand shows once).
    const vehicleLabel = formatVehicleBrandModel(
      lead.vehicleBrandName,
      lead.vehicleModel,
    );
    return (
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={onCellPress}
        style={{
          backgroundColor: LD.surface,
          borderRadius: rpx(14),
          marginHorizontal: rpx(16),
          marginBottom: rpx(12),
          paddingHorizontal: rpx(16),
          paddingVertical: rpx(15),
          ...cardShadowStyle(rpx),
        }}
      >
        {/* Identity: plate is the signature token; origin reads as its quiet
            subtitle. Status pill + creation time stack at the top-right. */}
        {/* Row 1: the plate (signature) + vehicle brand/model, and the status
            chip, share a line. */}
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flex: 1,
              marginRight: rpx(10),
            }}
          >
            <Typography.Text
              size={rpx(20)}
              color={LD.ink}
              bold
              letterSpacing={rpx(0.5)}
              numberOfLines={1}
            >
              {lead.licensePlateNo || '未知车牌'}
            </Typography.Text>
            {vehicleLabel ? (
              <Typography.Text
                size={rpx(13)}
                color={LD.steel}
                medium
                numberOfLines={1}
                style={{ flex: 1, marginLeft: rpx(10) }}
              >
                {vehicleLabel}
              </Typography.Text>
            ) : null}
          </View>
          <StatusPill
            label={LeadStatusLabel[lead.status]}
            tone={leadStatusTone(lead.status)}
          />
        </View>
        {/* Row 2: the source subtitle and the created time share a line. */}
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: rpx(4),
          }}
        >
          <Typography.Text
            size={rpx(12)}
            color="#8A8F98"
            numberOfLines={1}
            style={{ flex: 1, marginRight: rpx(10) }}
          >
            线索来源 · {LeadOriginLabel[lead.origin] ?? '其他'}
          </Typography.Text>
          <Typography.Text size={rpx(12)} color={LD.mist}>
            {formatLeadCreatedTime(lead.createdAt)}
          </Typography.Text>
        </View>
        {/* 到店意向 — a small left-aligned chip under the source line. */}
        <View style={{ flexDirection: 'row', marginTop: rpx(8) }}>
          <IntentionChip intention={lead.intention} />
        </View>

        {reachSummary ? (
          <>
            <Hairline />
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography.Text
                size={rpx(13)}
                color={LD.ink}
                numberOfLines={1}
                style={{ flex: 1 }}
              >
                <Typography.Text size={rpx(12)} color={LD.mist}>
                  {closed ? '结果' : '上次跟进'}{' '}
                </Typography.Text>
                <Typography.Text medium>{reachSummary}</Typography.Text>
              </Typography.Text>
              <Typography.Text
                size={rpx(12)}
                color={LD.mist}
                style={{ marginLeft: rpx(8) }}
              >
                {formatLeadReachTime(reachAt)}
              </Typography.Text>
            </View>
          </>
        ) : null}

        <Hairline />
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography.Text size={rpx(12)} color={LD.mist}>
            客户
          </Typography.Text>
          <Typography.Text size={rpx(14)} color={LD.ink} medium>
            {lead.customerName || '未填写客户'}
            {lead.customerPhone ? (
              <Typography.Text color={LD.steel}>
                {` · ${lead.customerPhone}`}
              </Typography.Text>
            ) : null}
          </Typography.Text>
        </View>

        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginTop: rpx(12),
          }}
        >
          <MetaBlock
            label="目标跟进"
            value={formatLeadDate(lead.targetServiceDate)}
          />
          <MetaBlock
            label="负责人"
            value={lead.assigneeName || '—'}
            align="right"
          />
        </View>
      </TouchableOpacity>
    );
  },
);
